import type { MotionGlyph } from "@/lib/generation-motion/glyphs"
import {
  createMotionPlanner,
  motionDotGrid,
  motionRandom,
  SPARK_IN_MS,
  SPARK_IN_SPREAD_MS,
  SPARK_IN_STAGGER_MS,
  SPARK_OUT_MS,
  SPARK_OUT_STAGGER_MS,
  type MotionGroup,
  type MotionPlanner,
} from "@/lib/generation-motion/plan"

/**
 * Рендер «Течения искр» в 2D-канвас. Работает в воркере (OffscreenCanvas) и на главном потоке.
 * Все лоадеры вкладки живут в одном MotionHost: один цикл кадров, общие спрайты и scratch-канвас,
 * невидимые сцены не рисуются.
 */

export type MotionTheme = "dark" | "light"
export type MotionStatus = "queued" | "processing"
export type MotionSceneConfig = { seed: number; status: MotionStatus; onMedia: boolean; reserveBottom: number }

export type MotionMessage =
  | { type: "glyphs"; glyphs: MotionGlyph[] }
  | { type: "add"; id: number; canvas: OffscreenCanvas | HTMLCanvasElement; config: MotionSceneConfig }
  | { type: "size"; id: number; width: number; height: number; scale: number }
  | { type: "config"; id: number; config: MotionSceneConfig }
  | { type: "visible"; id: number; visible: boolean }
  | { type: "finish"; id: number }
  | { type: "remove"; id: number }
  | { type: "page"; hidden: boolean }
  | { type: "reduced"; reduced: boolean }
  | { type: "theme"; theme: MotionTheme }

type AnyCanvas = OffscreenCanvas | HTMLCanvasElement
type Ctx = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D
type Rgb = readonly [number, number, number]

const PALETTES: Record<
  MotionTheme,
  { c1: Rgb; c2: Rgb; neutral: Rgb; neutralAlpha: number; lit: Rgb; letterGlow: boolean }
> = {
  dark: {
    c1: [139, 92, 246],
    c2: [56, 189, 248],
    neutral: [255, 255, 255],
    neutralAlpha: 0.13,
    lit: [185, 157, 250],
    letterGlow: true,
  },
  light: {
    c1: [194, 107, 74],
    c2: [150, 110, 170],
    neutral: [60, 45, 30],
    neutralAlpha: 0.18,
    lit: [194, 107, 74],
    letterGlow: false,
  },
}

const RIBBONS = [
  { top: 0.34, tone: "c1", alpha: 0.85, sway: 0.12, period: 7000, delay: -1500 },
  { top: 0.64, tone: "c2", alpha: 0.8, sway: 0.1, period: 9500, delay: -6000 },
  { top: 0.48, tone: "white", alpha: 0.4, sway: 0.16, period: 11000, delay: -3500 },
] as const

const QUEUED_TIME_SCALE = 1.7
const QUEUED_INTENSITY = 0.6
const GLOW_ALPHA = 0.35
/** Появление E1 — в такт `gen-motion-condense-in` в application.css. */
const ENTER_MS = 1000
const ENTER_GLOW_PEAK = 0.55
/** Буквы начинают идти, когда сетка уже села на место. */
const ENTER_LETTERS_MS = 650
const FINISH_MS = 900
const STATIC_CLOCK_MS = 2600

function rgba(c: Rgb, a: number) {
  return `rgba(${c[0]},${c[1]},${c[2]},${Math.max(0, Math.min(1, a))})`
}

function clamp01(v: number) {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

function smoothstep(v: number) {
  const t = clamp01(v)
  return t * t * (3 - 2 * t)
}

/** CSS cubic-bezier(x1, y1, x2, y2) для прогресса 0..1. */
function cubicBezier(x1: number, y1: number, x2: number, y2: number) {
  const bx = (t: number) => 3 * (1 - t) * (1 - t) * t * x1 + 3 * (1 - t) * t * t * x2 + t * t * t
  const by = (t: number) => 3 * (1 - t) * (1 - t) * t * y1 + 3 * (1 - t) * t * t * y2 + t * t * t
  return (x: number) => {
    if (x <= 0) return 0
    if (x >= 1) return 1
    let lo = 0
    let hi = 1
    let t = x
    for (let i = 0; i < 12; i++) {
      if (bx(t) < x) lo = t
      else hi = t
      t = (lo + hi) / 2
    }
    return by(t)
  }
}

const easeClay = cubicBezier(0.34, 1.3, 0.5, 1)
const easeSettle = cubicBezier(0.22, 0.8, 0.36, 1)

function makeCanvas(width: number, height: number): AnyCanvas {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(width, height)
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  return canvas
}

function context2d(canvas: AnyCanvas): Ctx | null {
  return (canvas as OffscreenCanvas).getContext("2d")
}

/** Разделяемые между сценами ресурсы потока: буквы, scratch-канвас, спрайты. */
class MotionShared {
  glyphs = new Map<string, MotionGlyph>()
  glyphWidths: Record<string, number> | null = null
  capHalf = 0.36
  theme: MotionTheme = "dark"
  private scratch: AnyCanvas | null = null
  private scratchCtx: Ctx | null = null
  private sprites = new Map<string, AnyCanvas>()
  private maskPatterns = new Map<string, CanvasPattern | null>()
  private cloudTile: AnyCanvas | null = null
  private cloudPattern: CanvasPattern | null = null

  setGlyphs(list: MotionGlyph[]) {
    this.glyphs = new Map(list.map((g) => [g.ch, g]))
    this.glyphWidths = Object.fromEntries(list.map((g) => [g.ch, g.widthRatio]))
    const e = this.glyphs.get("E")
    if (e) this.capHalf = e.capHalf
    for (const key of [...this.sprites.keys()]) if (key.startsWith("glow:")) this.sprites.delete(key)
  }

  palette() {
    return PALETTES[this.theme]
  }

  scratchFor(width: number, height: number) {
    if (!this.scratch || this.scratch.width < width || this.scratch.height < height) {
      const w = Math.max(width, this.scratch?.width ?? 0)
      const h = Math.max(height, this.scratch?.height ?? 0)
      this.scratch = makeCanvas(w, h)
      this.scratchCtx = context2d(this.scratch)
      this.maskPatterns.clear()
      this.cloudPattern = null
    }
    return this.scratchCtx
  }

  private sprite(key: string, build: () => AnyCanvas) {
    let s = this.sprites.get(key)
    if (!s) {
      s = build()
      this.sprites.set(key, s)
    }
    return s
  }

  /** Мягкий эллипс ленты течения; растягивается при отрисовке. */
  ribbonSprite(color: Rgb) {
    return this.sprite(`rib:${color.join(",")}`, () => {
      const size = 64
      const canvas = makeCanvas(size, size)
      const ctx = context2d(canvas)
      if (ctx) {
        const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
        g.addColorStop(0, rgba(color, 1))
        g.addColorStop(0.45, rgba(color, 0.62))
        g.addColorStop(1, rgba(color, 0))
        ctx.fillStyle = g
        ctx.fillRect(0, 0, size, size)
      }
      return canvas
    })
  }

  /** Круглая точка (device px) с полем в 1px. */
  dotSprite(color: Rgb, alpha: number, radiusPx: number) {
    const r = Math.max(0.5, Math.round(radiusPx * 4) / 4)
    return this.sprite(`dot:${color.join(",")}:${alpha}:${r}`, () => {
      const size = Math.ceil(r * 2) + 2
      const canvas = makeCanvas(size, size)
      const ctx = context2d(canvas)
      if (ctx) {
        ctx.fillStyle = rgba(color, alpha)
        ctx.beginPath()
        ctx.arc(size / 2, size / 2, r, 0, Math.PI * 2)
        ctx.fill()
      }
      return canvas
    })
  }

  /** Плитка сетки: одна точка в центре клетки step×step (device px). */
  dotTile(color: Rgb, alpha: number, stepPx: number, radiusPx: number) {
    return this.sprite(`tile:${color.join(",")}:${alpha.toFixed(3)}:${stepPx}:${radiusPx.toFixed(2)}`, () => {
      const canvas = makeCanvas(stepPx, stepPx)
      const ctx = context2d(canvas)
      if (ctx) {
        ctx.fillStyle = rgba(color, alpha)
        ctx.beginPath()
        ctx.arc(stepPx / 2, stepPx / 2, radiusPx, 0, Math.PI * 2)
        ctx.fill()
      }
      return canvas
    })
  }

  maskPattern(stepPx: number, radiusPx: number) {
    const ctx = this.scratchCtx
    if (!ctx) return null
    const key = `${stepPx}:${radiusPx.toFixed(2)}`
    if (!this.maskPatterns.has(key)) {
      this.maskPatterns.set(key, ctx.createPattern(this.dotTile([255, 255, 255], 1, stepPx, radiusPx), "repeat"))
    }
    return this.maskPatterns.get(key) ?? null
  }

  /** Бесшовные мягкие «облака» для бликов на серых точках. */
  cloud() {
    const ctx = this.scratchCtx
    if (!ctx) return null
    if (!this.cloudTile) {
      const size = 128
      const cells = 6
      const grid = Array.from({ length: cells * cells }, (_, i) => motionRandom(i * 3.17 + 11))
      const at = (x: number, y: number) => grid[((y + cells) % cells) * cells + ((x + cells) % cells)]
      const canvas = makeCanvas(size, size)
      const c = context2d(canvas)
      if (c) {
        const img = c.createImageData(size, size)
        for (let y = 0; y < size; y++) {
          for (let x = 0; x < size; x++) {
            const gx = (x / size) * cells
            const gy = (y / size) * cells
            const x0 = Math.floor(gx)
            const y0 = Math.floor(gy)
            const fx = smoothstep(gx - x0)
            const fy = smoothstep(gy - y0)
            const top = at(x0, y0) * (1 - fx) + at(x0 + 1, y0) * fx
            const bottom = at(x0, y0 + 1) * (1 - fx) + at(x0 + 1, y0 + 1) * fx
            const v = top * (1 - fy) + bottom * fy
            const a = clamp01(v * 3 - 1.45)
            const i = (y * size + x) * 4
            img.data[i] = img.data[i + 1] = img.data[i + 2] = 255
            img.data[i + 3] = Math.round(a * 255)
          }
        }
        c.putImageData(img, 0, 0)
      }
      this.cloudTile = canvas
    }
    if (!this.cloudPattern) this.cloudPattern = ctx.createPattern(this.cloudTile, "repeat")
    return this.cloudPattern
  }

  /** Мягкий ореол буквы цвета тона (тёмная тема). */
  glowSprite(glyph: MotionGlyph, color: Rgb) {
    return this.sprite(`glow:${glyph.ch}:${color.join(",")}`, () => {
      const size = 72
      const canvas = makeCanvas(size, size)
      const ctx = context2d(canvas)
      if (!ctx) return canvas
      const img = ctx.createImageData(size, size)
      const k = glyph.width / size
      const src = new Float32Array(size * size)
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          src[y * size + x] = glyph.alpha[Math.floor(y * k) * glyph.width + Math.floor(x * k)] / 255
        }
      }
      const radius = 3
      const blurred = new Float32Array(size * size)
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          let acc = 0
          let n = 0
          for (let dy = -radius; dy <= radius; dy++) {
            const yy = y + dy
            if (yy < 0 || yy >= size) continue
            for (let dx = -radius; dx <= radius; dx++) {
              const xx = x + dx
              if (xx < 0 || xx >= size) continue
              acc += src[yy * size + xx]
              n++
            }
          }
          blurred[y * size + x] = acc / n
        }
      }
      for (let i = 0; i < size * size; i++) {
        img.data[i * 4] = color[0]
        img.data[i * 4 + 1] = color[1]
        img.data[i * 4 + 2] = color[2]
        img.data[i * 4 + 3] = Math.round(blurred[i] * 255)
      }
      ctx.putImageData(img, 0, 0)
      return canvas
    })
  }
}

function sampleGlyph(g: MotionGlyph, u: number, v: number) {
  if (u < 0 || v < 0 || u >= g.width - 1 || v >= g.height - 1) return 0
  const x0 = u | 0
  const y0 = v | 0
  const fx = u - x0
  const fy = v - y0
  const i = y0 * g.width + x0
  const a = g.alpha
  const top = a[i] * (1 - fx) + a[i + 1] * fx
  const bottom = a[i + g.width] * (1 - fx) + a[i + g.width + 1] * fx
  return (top * (1 - fy) + bottom * fy) / 255
}

class MotionScene {
  visible = false
  private ctx: Ctx | null
  private width = 0
  private height = 0
  private scale = 1
  private step = 6
  private radius = 1
  private clock = 0
  private lastTime = 0
  private finishAt = 0
  private planner: MotionPlanner | null = null
  private plannerKey = ""
  private groupIndex = 0
  private groupStart = 0
  private basePattern: CanvasPattern | null = null
  private baseKey = ""

  constructor(
    private canvas: AnyCanvas,
    private config: MotionSceneConfig,
    private shared: MotionShared,
    private startedAt: number,
  ) {
    this.ctx = context2d(canvas)
  }

  setSize(width: number, height: number, scale: number) {
    this.width = width
    this.height = height
    this.scale = scale
    const grid = motionDotGrid(width, height)
    this.step = grid.step
    this.radius = grid.radius
    const cw = Math.max(1, Math.round(width * scale))
    const ch = Math.max(1, Math.round(height * scale))
    if (this.canvas.width !== cw) this.canvas.width = cw
    if (this.canvas.height !== ch) this.canvas.height = ch
    this.basePattern = null
  }

  setConfig(config: MotionSceneConfig) {
    if (config.seed !== this.config.seed || config.reserveBottom !== this.config.reserveBottom) this.plannerKey = ""
    this.config = config
    this.basePattern = null
  }

  finish(now: number) {
    this.finishAt = now
  }

  resetTime() {
    this.lastTime = 0
  }

  animating(paused: boolean) {
    return this.visible && !paused && this.width > 0 && this.height > 0
  }

  private ensurePlanner() {
    if (!this.shared.glyphWidths) return null
    const key = `${this.width}x${this.height}:${this.config.seed}:${this.config.reserveBottom}:${this.shared.glyphs.size}`
    if (key !== this.plannerKey) {
      this.plannerKey = key
      this.planner = createMotionPlanner({
        width: this.width,
        height: this.height,
        seed: this.config.seed,
        glyphWidths: this.shared.glyphWidths,
        capHalf: this.shared.capHalf,
        reserveBottom: this.config.reserveBottom,
      })
      this.groupStart = Math.max(this.clock, ENTER_LETTERS_MS)
    }
    return this.planner
  }

  frame(now: number) {
    const dt = this.lastTime ? Math.min(100, now - this.lastTime) : 0
    this.lastTime = now
    const timeScale = this.config.status === "queued" ? QUEUED_TIME_SCALE : 1
    this.clock += dt / timeScale
    this.draw(now, true)
  }

  drawStatic() {
    const saved = this.clock
    this.clock = STATIC_CLOCK_MS
    this.draw(0, false)
    this.clock = saved
  }

  private draw(now: number, withLetters: boolean) {
    const ctx = this.ctx
    if (!ctx || this.width <= 0 || this.height <= 0) return
    const k = this.scale
    const cw = this.canvas.width
    const ch = this.canvas.height
    const pal = this.shared.palette()
    const intensity = this.config.status === "queued" ? QUEUED_INTENSITY : 1
    const dn = this.config.onMedia ? pal.neutralAlpha * 1.3 : pal.neutralAlpha
    const stepPx = Math.max(1, Math.round(this.step * k))
    const radiusPx = this.radius * k

    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.globalCompositeOperation = "source-over"
    ctx.globalAlpha = 1
    ctx.clearRect(0, 0, cw, ch)

    const baseKey = `${pal.neutral.join(",")}:${dn}:${stepPx}:${radiusPx}`
    if (!this.basePattern || baseKey !== this.baseKey) {
      this.baseKey = baseKey
      this.basePattern = ctx.createPattern(this.shared.dotTile(pal.neutral, dn, stepPx, radiusPx), "repeat")
    }
    if (this.basePattern) {
      ctx.fillStyle = this.basePattern
      ctx.fillRect(0, 0, cw, ch)
    }

    let glow = GLOW_ALPHA
    if (this.finishAt && now) {
      const f = (now - this.finishAt) / FINISH_MS
      glow = f < 0.25 ? GLOW_ALPHA + (1 - GLOW_ALPHA) * (f / 0.25) : Math.max(0, 1 - (f - 0.25) / 0.75)
    } else if (now && now - this.startedAt < ENTER_MS) {
      const f = Math.max(0, now - this.startedAt) / ENTER_MS
      glow =
        f < ENTER_GLOW_PEAK
          ? f / ENTER_GLOW_PEAK
          : 1 - (1 - GLOW_ALPHA) * ((f - ENTER_GLOW_PEAK) / (1 - ENTER_GLOW_PEAK))
    }
    this.drawRibbons(ctx, glow)
    this.drawDotted(cw, ch, stepPx, radiusPx, dn, intensity)
    if (withLetters) this.drawLetters(ctx, intensity)
  }

  private drawRibbons(ctx: Ctx, alphaMul: number) {
    const pal = this.shared.palette()
    const k = this.scale
    const w = this.width
    const h = this.height
    const s = Math.min(w, h)
    for (const r of RIBBONS) {
      const local = (((this.clock - r.delay) % r.period) + r.period) % r.period
      const phase = local / r.period
      const env = phase < 0.18 ? phase / 0.18 : phase > 0.82 ? (1 - phase) / 0.18 : 1
      const half = local / (r.period / 2)
      const e = smoothstep(half <= 1 ? half : 2 - half)
      const x = w / 2 - 0.95 * w + 1.9 * w * phase
      const y = r.top * h + s * r.sway * (-1 + 2 * e)
      const angle = ((-6 + 12 * e) * Math.PI) / 180
      const color = r.tone === "c1" ? pal.c1 : r.tone === "c2" ? pal.c2 : ([255, 255, 255] as const)
      ctx.save()
      ctx.globalAlpha = r.alpha * env * alphaMul
      ctx.translate(x * k, y * k)
      ctx.rotate(angle)
      ctx.drawImage(this.shared.ribbonSprite(color), -0.75 * s * k, -0.25 * s * k, 1.5 * s * k, 0.5 * s * k)
      ctx.restore()
    }
  }

  /** Блики и ленты, видимые только сквозь точки сетки. */
  private drawDotted(cw: number, ch: number, stepPx: number, radiusPx: number, dn: number, intensity: number) {
    const ctx = this.ctx
    const fx = this.shared.scratchFor(cw, ch)
    if (!ctx || !fx) return
    const pal = this.shared.palette()
    const k = this.scale
    fx.setTransform(1, 0, 0, 1, 0, 0)
    fx.globalCompositeOperation = "source-over"
    fx.globalAlpha = 1
    fx.clearRect(0, 0, cw, ch)

    const cloud = this.shared.cloud()
    if (cloud) {
      const gl = Math.max(120, Math.round(Math.min(this.width, this.height) * 1.3))
      const tileScale = (gl * k) / 128
      const layers = [
        { dx: gl * ((this.clock / 10000) % 1), dy: 0, alpha: 1 },
        { dx: 0, dy: -gl * ((this.clock / 14000) % 1), alpha: 0.7 },
      ]
      fx.fillStyle = cloud
      for (const layer of layers) {
        fx.save()
        fx.globalAlpha = layer.alpha
        fx.translate(layer.dx * k, layer.dy * k)
        fx.scale(tileScale, tileScale)
        fx.fillRect(-256, -256, cw / tileScale + 512, ch / tileScale + 512)
        fx.restore()
      }
      fx.globalCompositeOperation = "source-in"
      fx.fillStyle = rgba(pal.neutral, Math.min(1, dn * 3.2) * intensity)
      fx.fillRect(0, 0, cw, ch)
      fx.globalCompositeOperation = "source-over"
    }

    this.drawRibbons(fx, intensity)

    const mask = this.shared.maskPattern(stepPx, radiusPx)
    if (mask) {
      fx.globalCompositeOperation = "destination-in"
      fx.globalAlpha = 1
      fx.fillStyle = mask
      fx.fillRect(0, 0, cw, ch)
      fx.globalCompositeOperation = "source-over"
    }
    ctx.drawImage(fx.canvas as CanvasImageSource, 0, 0, cw, ch, 0, 0, cw, ch)
  }

  private drawLetters(ctx: Ctx, intensity: number) {
    const planner = this.ensurePlanner()
    if (!planner || !planner.lettersEnabled) return
    while (this.clock - this.groupStart >= planner.group(this.groupIndex).duration) {
      this.groupStart += planner.group(this.groupIndex).duration
      this.groupIndex++
    }
    const current = planner.group(this.groupIndex)
    const tNow = this.clock - this.groupStart
    if (this.groupIndex > 0) {
      const prev = planner.group(this.groupIndex - 1)
      this.drawGroup(ctx, planner, prev, prev.duration + tNow, tNow, intensity)
    }
    this.drawGroup(ctx, planner, current, tNow, -1, intensity)
  }

  /** age — мс с появления группы; outT — мс с начала угасания (-1, пока группа текущая). */
  private drawGroup(
    ctx: Ctx,
    planner: MotionPlanner,
    group: MotionGroup,
    age: number,
    outT: number,
    intensity: number,
  ) {
    const pal = this.shared.palette()
    const k = this.scale
    const step = this.step
    const cols = Math.floor(this.width / step)
    const rows = Math.floor(this.height / step)
    const life = group.duration + planner.group(group.index + 1).duration
    const driftNow = group.drift * clamp01(age / life)
    const seed = this.config.seed + group.index * 131
    const dot = this.shared.dotSprite(pal.lit, 1, step * 0.27 * k)
    const dotSize = dot.width

    group.letters.forEach((letter, j) => {
      const glyph = this.shared.glyphs.get(letter.ch)
      if (!glyph) return
      const sc = letter.fontSize / glyph.fontPx
      const cx = letter.cx + driftNow
      const cy = letter.cy + planner.bob * -Math.cos((Math.PI * (age + letter.bobPhase)) / letter.bobPeriod)
      const hw = (glyph.widthRatio * letter.fontSize) / 2 + step
      const hh = glyph.capHalf * letter.fontSize + step
      const inT = age - j * SPARK_IN_STAGGER_MS

      if (pal.letterGlow) {
        const env = outT < 0 ? clamp01(inT / 700) : 1 - clamp01((outT - j * SPARK_OUT_STAGGER_MS) / (SPARK_OUT_MS + 50))
        if (env > 0.01) {
          ctx.globalAlpha = 0.5 * env * intensity
          const gs = this.shared.glowSprite(glyph, pal.c1)
          const left = (cx - glyph.cx * sc) * k
          const top = (cy - glyph.cy * sc) * k
          ctx.drawImage(gs, left, top, glyph.width * sc * k, glyph.height * sc * k)
        }
      }

      const gx0 = Math.max(0, Math.floor((cx - hw) / step))
      const gx1 = Math.min(cols, Math.ceil((cx + hw) / step))
      const gy0 = Math.max(0, Math.floor((cy - hh) / step))
      const gy1 = Math.min(rows, Math.ceil((cy + hh) / step))
      for (let gx = gx0; gx < gx1; gx++) {
        const x = (gx + 0.5) * step
        const u = glyph.cx + (x - cx) / sc
        for (let gy = gy0; gy < gy1; gy++) {
          const y = (gy + 0.5) * step
          const cover = sampleGlyph(glyph, u, glyph.cy + (y - cy) / sc)
          if (cover < 0.04) continue
          const rand = motionRandom(seed + gx * 7.13 + gy * 3.71 + j * 17)
          let op: number
          let scale: number
          if (outT < 0) {
            const p = clamp01((inT - rand * SPARK_IN_SPREAD_MS) / SPARK_IN_MS)
            if (p <= 0) continue
            scale = 0.15 + 0.85 * easeClay(p)
            op = Math.min(1, p / 0.5)
          } else {
            const rel = clamp01((x - (cx - hw)) / (2 * hw))
            const delay = j * SPARK_OUT_STAGGER_MS + (1 - rel) * 240 + motionRandom(seed + gx * 1.7 + gy * 9.1) * 160
            const p = clamp01((outT - delay) / SPARK_OUT_MS)
            if (p >= 1) continue
            const e = easeSettle(p)
            scale = 1 - 0.65 * e
            op = 1 - e
          }
          ctx.globalAlpha = cover * op * intensity
          const size = dotSize * scale
          ctx.drawImage(dot, x * k - size / 2, y * k - size / 2, size, size)
        }
      }
    })
    ctx.globalAlpha = 1
  }
}

type Scheduler = (cb: (time: number) => void) => void

/** Все сцены потока и общий цикл кадров. */
export class MotionHost {
  private shared = new MotionShared()
  private scenes = new Map<number, MotionScene>()
  private pageHidden = false
  private reduced = false
  private scheduled = false
  private lastFrame = 0

  constructor(
    private schedule: Scheduler,
    private now: () => number,
  ) {}

  handle(msg: MotionMessage) {
    switch (msg.type) {
      case "glyphs":
        this.shared.setGlyphs(msg.glyphs)
        break
      case "add":
        this.scenes.set(msg.id, new MotionScene(msg.canvas, msg.config, this.shared, this.now()))
        break
      case "size":
        this.scenes.get(msg.id)?.setSize(msg.width, msg.height, msg.scale)
        this.redrawStatic(msg.id)
        break
      case "config":
        this.scenes.get(msg.id)?.setConfig(msg.config)
        this.redrawStatic(msg.id)
        break
      case "visible": {
        const scene = this.scenes.get(msg.id)
        if (scene) {
          scene.visible = msg.visible
          scene.resetTime()
        }
        this.redrawStatic(msg.id)
        break
      }
      case "finish":
        this.scenes.get(msg.id)?.finish(this.now())
        break
      case "remove":
        this.scenes.delete(msg.id)
        break
      case "page":
        this.pageHidden = msg.hidden
        for (const scene of this.scenes.values()) scene.resetTime()
        break
      case "reduced":
        this.reduced = msg.reduced
        for (const id of this.scenes.keys()) this.redrawStatic(id)
        break
      case "theme":
        this.shared.theme = msg.theme
        for (const id of this.scenes.keys()) this.redrawStatic(id)
        break
    }
    this.kick()
  }

  private redrawStatic(id: number) {
    if (!this.reduced) return
    this.scenes.get(id)?.drawStatic()
  }

  private paused() {
    return this.pageHidden || this.reduced
  }

  private kick() {
    if (this.scheduled) return
    for (const scene of this.scenes.values()) {
      if (scene.animating(this.paused())) {
        this.scheduled = true
        this.schedule(this.tick)
        return
      }
    }
  }

  private tick = (time: number) => {
    this.scheduled = false
    const live: MotionScene[] = []
    for (const scene of this.scenes.values()) if (scene.animating(this.paused())) live.push(scene)
    if (!live.length) return
    const interval = live.length <= 4 ? 1000 / 60 : 1000 / 30
    if (time - this.lastFrame >= interval - 3) {
      this.lastFrame = time
      for (const scene of live) scene.frame(time)
    }
    this.scheduled = true
    this.schedule(this.tick)
  }
}
